import React from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, Seo, SITE_URL } from "@shared/components";
import { getCardTitle, getPagesByCluster, type SeoCluster } from "@shared/config/seo-pages";
import { FaqSection } from "@widgets/FaqSection/FaqSection";
import { ArrowRight } from "lucide-react";

interface CatalogPageProps {
  cluster: SeoCluster;
  path: string;
  metaTitle: string;
  metaDescription: string;
  breadcrumb: string;
  title: React.ReactNode;
  subtitle: string;
  intro: React.ReactNode;
  listHeading: string;
  faq: { question: string; answer: string }[];
}

/**
 * Страница-хаб тематической группы: собирает все дочерние посадочные,
 * ранжируется под общий запрос и распределяет ссылочный вес вниз.
 */
export const CatalogPage: React.FC<CatalogPageProps> = ({
  cluster,
  path,
  metaTitle,
  metaDescription,
  breadcrumb,
  title,
  subtitle,
  intro,
  listHeading,
  faq
}) => {
  const pages = getPagesByCluster(cluster);

  const itemListSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: metaTitle,
    description: metaDescription,
    url: `${SITE_URL}${path}`,
    inLanguage: "ru-RU",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: pages.length,
      itemListElement: pages.map((page, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: getCardTitle(page),
        url: `${SITE_URL}${page.path}`
      }))
    }
  };

  const faqSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer }
    }))
  };

  return (
    <div className="overflow-hidden">
      <Seo title={metaTitle} description={metaDescription} path={path} jsonLd={[itemListSchema, faqSchema]} />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: breadcrumb }]} />

      <section className="border-b border-border py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl text-balance">{title}</h1>
            <p className="text-lg text-muted-foreground text-pretty">{subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-muted/30">
        <div className="container max-w-4xl px-4 md:px-6">
          <div className="prose prose-sm md:prose-base dark:prose-invert text-muted-foreground space-y-4">{intro}</div>
        </div>
      </section>

      <section className="py-16">
        <div className="container px-4 md:px-6">
          <div className="mx-auto max-w-5xl">
            <h2 className="mb-8 text-2xl font-bold tracking-tight text-foreground md:text-3xl">{listHeading}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pages.map(page => (
                <Link
                  key={page.path}
                  to={page.path}
                  className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
                >
                  <div className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                    {getCardTitle(page)}
                    <ArrowRight className="h-4 w-4 shrink-0 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  <p className="text-sm text-muted-foreground">{page.customSubtitle}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FaqSection items={faq} />
    </div>
  );
};
